"use client";

import type * as React from "react";
import { cn } from "@/lib/utils";
import { Field, FieldDescription, FieldError, FieldLabel, useFieldWrapperContext } from "./field-wrapper";
import { SwitchItem } from "./switch-item";
import type { FieldBindingProps } from "./types";

export interface SwitchListOption {
  value: string;
  label: React.ReactNode;
  /** Rendered beneath the label, inside the clickable item. */
  description?: React.ReactNode;
  disabled?: boolean;
}

export interface SwitchListFieldProps extends FieldBindingProps<string[]> {
  label?: string;
  description?: string;
  options: SwitchListOption[];
  /** Shown in place of the list when there are no options. */
  empty?: React.ReactNode;
  disabled?: boolean;
  isRequired?: boolean;
  className?: string;
}

/** Multi-select as a list of switch items: `value` holds the values switched on, in option order. */
export function SwitchListField({
  value,
  onChange,
  onBlur,
  errors,
  isTouched,
  isRequired,
  label,
  description,
  options,
  empty,
  disabled,
  className,
}: SwitchListFieldProps) {
  return (
    <Field errors={errors} isTouched={isTouched} isRequired={isRequired} className={cn("space-y-1.5", className)}>
      {label && <FieldLabel>{label}</FieldLabel>}
      {description && <FieldDescription>{description}</FieldDescription>}
      {options.length === 0 ? (
        empty && <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <SwitchItems value={value} onChange={onChange} onBlur={onBlur} options={options} disabled={disabled} />
      )}
      <FieldError />
    </Field>
  );
}

function SwitchItems({
  value,
  onChange,
  onBlur,
  options,
  disabled,
}: Pick<SwitchListFieldProps, "value" | "onChange" | "onBlur" | "options" | "disabled">) {
  const { id, hasError } = useFieldWrapperContext();
  const on = new Set(value);
  const toggle = (option: string, next: boolean) => {
    const after = new Set(on);
    if (next) after.add(option);
    else after.delete(option);
    // Values with no item (an option since removed) are kept, not silently dropped.
    const listed = new Set(options.map((o) => o.value));
    onChange([
      ...options.filter((o) => after.has(o.value)).map((o) => o.value),
      ...value.filter((v) => !listed.has(v)),
    ]);
    onBlur();
  };
  return (
    <fieldset id={id} aria-invalid={hasError} className="m-0 min-w-0 divide-y overflow-hidden rounded-lg border p-0">
      {options.map((option) => (
        <SwitchItem
          key={option.value}
          id={`${id}-${option.value}`}
          checked={on.has(option.value)}
          onCheckedChange={(next) => toggle(option.value, next)}
          label={option.label}
          description={option.description}
          disabled={disabled || option.disabled}
          dimWhenOff
        />
      ))}
    </fieldset>
  );
}
